import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import '../../assets/css/maintenance.scss';

const Maintenance = () => {
  const { language } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [maintenanceEnd, setMaintenanceEnd] = useState(null);

  // Helper function for development - expose to window in dev mode
  useEffect(() => {
    if (window.APP_CONFIG?.deployment?.environment === 'development' || 
        window.location.hostname === 'localhost') {
      
      // Expose a helper function to disable maintenance mode in development
      window.disableMaintenance = () => {
        localStorage.setItem('maintenance_disabled', 'true');
        console.log('Maintenance mode disabled. Refreshing page...');
        window.location.reload();
      };
      
      // Expose a helper function to re-enable maintenance mode
      window.enableMaintenance = () => {
        localStorage.removeItem('maintenance_disabled');
        console.log('Maintenance mode re-enabled. Refreshing page...');
        window.location.reload();
      };
      
      console.log('Development helpers available:');
      console.log('- window.disableMaintenance() - Disable maintenance mode');
      console.log('- window.enableMaintenance() - Re-enable maintenance mode');
    }
  }, []);

  useEffect(() => {
    
    // Get maintenance end time from configuration
    const getMaintenanceEndTime = () => {
      try {
        // Check for maintenance end time from various sources
        const configEndTime = window.APP_CONFIG?.maintenance?.endTime || 
                             window.RUNTIME_CONFIG?.maintenance?.endTime ||
                             global.config?.maintenance?.endTime;
        
        // Return null if no end time is configured
        if (!configEndTime) {
          return null;
        }
        
        // Handle both "yyyy-MM-dd HH:mm:ss" UTC format and ISO format
        let endTimeDate;
        
        if (typeof configEndTime === 'string') {
          // Check if it's already in ISO format
          if (configEndTime.includes('T') || configEndTime.includes('Z')) {
            endTimeDate = new Date(configEndTime);
          } else {
            // Parse "yyyy-MM-dd HH:mm:ss" format as UTC
            const isoString = configEndTime.replace(' ', 'T') + 'Z';
            endTimeDate = new Date(isoString);
          }
        } else if (configEndTime instanceof Date) {
          endTimeDate = configEndTime;
        } else {
          throw new Error('Invalid endTime format');
        }
        
        // Validate the parsed date
        if (isNaN(endTimeDate.getTime())) {
          throw new Error('Invalid date');
        }
        
        return endTimeDate;
      } catch (error) {
        console.error('Error getting maintenance end time:', error);
        return null;
      }
    };

    const endTime = getMaintenanceEndTime();
    setMaintenanceEnd(endTime);

    // If no end time is configured, don't set up countdown
    if (!endTime) {
      setTimeRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const timeDiff = endTime - now;

      if (timeDiff <= 0) {
        setTimeRemaining(null);
        // Don't auto-reload if maintenance is still enabled
        // The system admin needs to manually disable maintenance mode
        return;
      }

      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      setTimeRemaining({ hours, minutes, seconds });
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  // Helper function to get timezone information
  const getTimezoneInfo = () => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const shortTimezone = new Date().toLocaleDateString('en-US', { 
        timeZoneName: 'short' 
      }).split(', ')[1];
      return { timezone, shortTimezone };
    } catch (error) {
      return { timezone: 'Local Time', shortTimezone: 'Local' };
    }
  };

  // Helper function to format local time with timezone
  const formatLocalTime = (date) => {
    if (!date) return '';
    
    try {
      const { shortTimezone } = getTimezoneInfo();
      return `${date.toLocaleString()} (${shortTimezone})`;
    } catch (error) {
      return date.toLocaleString();
    }
  };

  // Helper function to format UTC time
  const formatUTCTime = (date) => {
    if (!date) return '';
    
    try {
      return `${date.toISOString().replace('T', ' ').substring(0, 19)} UTC`;
    } catch (error) {
      return date.toUTCString();
    }
  };

  const getMaintenanceMessage = () => {
    return window.APP_CONFIG?.maintenance?.message || 
           window.RUNTIME_CONFIG?.maintenance?.message ||
           global.config?.maintenance?.message ||
           language.maintenance?.defaultMessage ||
           'We are currently performing system maintenance to improve your experience. Please check back soon.';
  };

  return (
    <div className="maintenance-page">
      <div className="maintenance-container">
        <div className="maintenance-icon">
          <i className="fa fa-wrench fa-3x"></i>
        </div>
        
        <h1 className="maintenance-title">
          {language.maintenance?.title || 'System Under Maintenance'}
        </h1>
        
        <div className="maintenance-message">
          <p>{getMaintenanceMessage()}</p>
        </div>

        {timeRemaining && maintenanceEnd ? (
          <div className="maintenance-countdown">
            <h2>
              {language.maintenance?.estimatedTime || 'Estimated time remaining:'} 
              <span className="timezone-info"> ({getTimezoneInfo().shortTimezone})</span>
            </h2>
            <div className="countdown-display">
              <div className="countdown-segment">
                <span className="countdown-number">{timeRemaining.hours.toString().padStart(2, '0')}</span>
                <span className="countdown-label">{language.maintenance?.hours || 'Hours'}</span>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-segment">
                <span className="countdown-number">{timeRemaining.minutes.toString().padStart(2, '0')}</span>
                <span className="countdown-label">{language.maintenance?.minutes || 'Minutes'}</span>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-segment">
                <span className="countdown-number">{timeRemaining.seconds.toString().padStart(2, '0')}</span>
                <span className="countdown-label">{language.maintenance?.seconds || 'Seconds'}</span>
              </div>
            </div>
            
            <p className="maintenance-time-note">
              {language.maintenance?.expectedBack || 'Expected to be back at:'} {' '}
              <strong>{formatLocalTime(maintenanceEnd)}</strong>
              <br />
              <small className="utc-time">UTC: {formatUTCTime(maintenanceEnd)}</small>
            </p>
          </div>
        ) : (
          <div className="maintenance-no-countdown">
            <div className="maintenance-status">
              <i className="fa fa-cog fa-spin fa-2x"></i>
              <h2>{language.maintenance?.inProgress || 'Maintenance in Progress'}</h2>
              <p className="status-message">
                {maintenanceEnd && !timeRemaining ? 
                  (language.maintenance?.extendedMaintenance || 'Maintenance is taking longer than expected. Thank you for your patience.') :
                  (language.maintenance?.noTimeEstimate || 'We are working to restore service as quickly as possible.')
                }
              </p>
              {maintenanceEnd && !timeRemaining && (
                <p className="original-time-note">
                  <small>
                    {language.maintenance?.originallyScheduled || 'Originally scheduled to complete at:'} {' '}
                    <strong>{formatLocalTime(maintenanceEnd)}</strong>
                  </small>
                </p>
              )}
            </div>
          </div>
        )}

        <div className="maintenance-contact">
          <p>{language.maintenance?.contactInfo || 'If you need immediate assistance, please contact our support team.'}</p>
          <div className="contact-links">
            <a href="mailto:support@evidenceiq.com" className="contact-link">
              <i className="fa fa-envelope"></i>
              {language.maintenance?.email || 'Email Support'}
            </a>
          </div>
        </div>

        <div className="maintenance-refresh">
          <button 
            className="refresh-button" 
            onClick={() => window.location.reload()}
            title={language.maintenance?.refreshPage || 'Refresh page'}
          >
            <i className="fa fa-refresh"></i>
            {language.maintenance?.refresh || 'Refresh'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
